import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from 'components/Nav/nav.module.scss'
import clsx from 'clsx'
import Container from 'gu-ui-kit/Container'
import Icon from 'gu-ui-kit/Icon'
import Tooltip from 'gu-ui-kit/Tooltip'
import Typography from 'gu-ui-kit/Typography'
import config from 'components/Nav/config'
import { NavLink } from 'react-router-dom'
import Nav from 'components/Nav'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { findMatchedNavRoutes } from 'utils/findMatchedRoutes'
import { matchPath, useLocation } from 'react-router'
import { SET_DRAWER_STATE } from 'modules/App/types'
import { useDispatch } from 'react-redux'
import { preloadDomainForPathname } from 'store/domains/utils'
import { useNavKeyboard } from 'components/Nav/NavKeyboardContext'

const FLYOUT_CLOSE_DURATION_MS = 160

const SubLevel = ({
  label,
  label_key,
  exact,
  path,
  icon,
  routes = [],
  level,
  routeKey = '',
  itemKey,
  parentKey = null,
  itemIndex = 0,
  collapse,
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const pathGen = useGeneratePathI18n()
  const displayLabel = label ?? t(label_key)
  const location = useLocation()
  const navKeyboard = useNavKeyboard()
  const to = Array.isArray(path) ? path[0] : path
  const resolvedPath = path ? pathGen(to) : null
  const matchedDescendants = useMemo(
    () => findMatchedNavRoutes(routes, location.pathname),
    [routes, location.pathname],
  )
  const selfMatched = useMemo(
    () =>
      resolvedPath ? matchPath(location.pathname, { path: resolvedPath, exact }) : null,
    [resolvedPath, location.pathname, exact],
  )
  const isBranchActive = matchedDescendants.length > 0 || Boolean(selfMatched)
  const [open, setOpen] = useState(routes.length > 0 && isBranchActive)
  const [flyoutVisible, setFlyoutVisible] = useState(false)
  const [flyoutClosing, setFlyoutClosing] = useState(false)
  const [flyoutPosition, setFlyoutPosition] = useState({ top: 0, left: 0 })
  const [tooltipFromFocus, setTooltipFromFocus] = useState(false)
  const itemRef = useRef(null)
  const linkRef = useRef(null)
  const closeFlyoutTimerRef = useRef(null)
  const pendingFocusChildRef = useRef(false)
  const openRef = useRef(open)
  const flyoutVisibleRef = useRef(flyoutVisible)
  const showFlyout = collapse && routes.length > 0
  const showTooltip = collapse && routes.length === 0
  const isFlyoutActive = flyoutVisible || flyoutClosing
  const hasChildren = routes.length > 0

  openRef.current = open
  flyoutVisibleRef.current = flyoutVisible

  const updateFlyoutPosition = useCallback(() => {
    if (!itemRef.current) {
      return
    }

    const rect = itemRef.current.getBoundingClientRect()
    setFlyoutPosition({
      top: rect.top,
      left: rect.right + 8,
    })
  }, [])

  const clearCloseFlyoutTimer = useCallback(() => {
    if (closeFlyoutTimerRef.current) {
      clearTimeout(closeFlyoutTimerRef.current)
      closeFlyoutTimerRef.current = null
    }
  }, [])

  const openFlyoutPanel = useCallback(() => {
    clearCloseFlyoutTimer()
    setFlyoutClosing(false)
    updateFlyoutPosition()
    setFlyoutVisible(true)
  }, [clearCloseFlyoutTimer, updateFlyoutPosition])

  const closeFlyoutPanel = useCallback(() => {
    clearCloseFlyoutTimer()
    setFlyoutClosing(true)
    closeFlyoutTimerRef.current = setTimeout(() => {
      setFlyoutVisible(false)
      setFlyoutClosing(false)
      closeFlyoutTimerRef.current = null
    }, FLYOUT_CLOSE_DURATION_MS)
  }, [clearCloseFlyoutTimer])

  useEffect(() => () => clearCloseFlyoutTimer(), [clearCloseFlyoutTimer])

  useEffect(() => {
    if (!showFlyout) {
      clearCloseFlyoutTimer()
      setFlyoutVisible(false)
      setFlyoutClosing(false)
    }
  }, [clearCloseFlyoutTimer, showFlyout])

  useEffect(() => {
    if (!showFlyout || !isFlyoutActive) {
      return undefined
    }

    const handleReposition = () => updateFlyoutPosition()
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeFlyoutPanel()
        navKeyboard?.focusKey?.(itemKey)
      }
    }

    window.addEventListener('resize', handleReposition)
    window.addEventListener('scroll', handleReposition, true)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleReposition, true)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [closeFlyoutPanel, isFlyoutActive, itemKey, navKeyboard, showFlyout, updateFlyoutPosition])

  const handleFlyoutMouseEnter = useCallback(() => {
    if (showFlyout) {
      openFlyoutPanel()
    }
  }, [openFlyoutPanel, showFlyout])

  const handleFlyoutMouseLeave = useCallback(() => {
    if (!showFlyout) return
    // Keep flyout while keyboard focus is inside (parent or submenu).
    if (itemRef.current?.contains(document.activeElement)) return
    closeFlyoutPanel()
  }, [closeFlyoutPanel, showFlyout])

  // Collapsed sidebar: keyboard focus mirrors hover (tooltip label / flyout submenu).
  const handleItemFocus = useCallback(() => {
    if (showFlyout) {
      openFlyoutPanel()
    }
    if (showTooltip) {
      setTooltipFromFocus(true)
    }
  }, [openFlyoutPanel, showFlyout, showTooltip])

  const handleItemBlur = useCallback(
    (event) => {
      if (itemRef.current?.contains(event.relatedTarget)) {
        return
      }

      if (showFlyout) {
        closeFlyoutPanel()
      }
      if (showTooltip) {
        setTooltipFromFocus(false)
      }
    },
    [closeFlyoutPanel, showFlyout, showTooltip],
  )

  useEffect(() => {
    if (!collapse) {
      setTooltipFromFocus(false)
    }
  }, [collapse])

  const itemConfig = config?.[routeKey]
  const LinkComponent = itemConfig?.component || (routes?.length > 0 ? 'div' : NavLink)
  const isNativeOpener = LinkComponent === 'div' || Boolean(itemConfig?.native)

  useEffect(() => {
    if (routes.length > 0) {
      setOpen((prevOpen) => (prevOpen === isBranchActive ? prevOpen : isBranchActive))
    }
  }, [location.pathname, routes.length, isBranchActive])

  const expandBranch = useCallback(() => {
    if (!hasChildren) return false

    if (showFlyout) {
      if (!flyoutVisibleRef.current) {
        pendingFocusChildRef.current = true
        openFlyoutPanel()
        return true
      }
      return false
    }

    if (!openRef.current) {
      pendingFocusChildRef.current = true
      dispatch({ type: SET_DRAWER_STATE, payload: { collapsed: false } })
      setOpen(true)
      return true
    }

    return false
  }, [dispatch, hasChildren, openFlyoutPanel, showFlyout])

  const collapseBranch = useCallback(() => {
    if (showFlyout && flyoutVisibleRef.current) {
      closeFlyoutPanel()
      return true
    }

    if (!showFlyout && openRef.current) {
      setOpen(false)
      return true
    }

    return false
  }, [closeFlyoutPanel, showFlyout])

  useEffect(() => {
    if (!navKeyboard?.register) return undefined

    return navKeyboard.register({
      key: itemKey,
      parentKey,
      index: itemIndex,
      level,
      isActive: isBranchActive,
      hasChildren,
      getElement: () => linkRef.current,
      getOpen: () => (showFlyout ? flyoutVisibleRef.current : openRef.current),
      expand: expandBranch,
      collapse: collapseBranch,
    })
    // Depend on stable register + item fields — not the whole context value
    // (tabStopKey changes would otherwise re-register and reset roving tab stop).
  }, [
    collapseBranch,
    expandBranch,
    hasChildren,
    isBranchActive,
    itemIndex,
    itemKey,
    level,
    navKeyboard?.register,
    parentKey,
    showFlyout,
  ])

  useEffect(() => {
    if (!pendingFocusChildRef.current || !navKeyboard) return

    const ready = showFlyout ? flyoutVisible : open
    if (!ready) return

    pendingFocusChildRef.current = false
    const children = navKeyboard.getChildren(itemKey)
    if (children[0]) {
      navKeyboard.focusKey(children[0].key)
    }
  }, [flyoutVisible, itemKey, navKeyboard, open, showFlyout])

  const handleKeyDown = useCallback(
    (event) => {
      if (!navKeyboard) return

      const siblings = navKeyboard.getSiblings(parentKey)
      const index = siblings.findIndex((entry) => entry.key === itemKey)
      if (index < 0) return

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault()
          const next = siblings[(index + 1) % siblings.length]
          navKeyboard.focusKey(next.key)
          break
        }
        case 'ArrowUp': {
          event.preventDefault()
          const prev = siblings[(index - 1 + siblings.length) % siblings.length]
          navKeyboard.focusKey(prev.key)
          break
        }
        case 'ArrowRight': {
          event.preventDefault()
          if (!hasChildren) break
          if (!expandBranch()) {
            const children = navKeyboard.getChildren(itemKey)
            if (children[0]) navKeyboard.focusKey(children[0].key)
          }
          break
        }
        case 'ArrowLeft': {
          event.preventDefault()
          // On an open parent: close its submenu and keep focus here.
          if (collapseBranch()) {
            navKeyboard.focusKey(itemKey)
            break
          }
          // In a submenu: close the parent branch and move focus to the parent.
          if (parentKey != null) {
            navKeyboard.getEntry(parentKey)?.collapse?.()
            navKeyboard.focusKey(parentKey)
          }
          break
        }
        case ' ': {
          if (!hasChildren) break
          event.preventDefault()
          if (showFlyout ? flyoutVisibleRef.current : openRef.current) {
            collapseBranch()
            navKeyboard.focusKey(itemKey)
          } else {
            expandBranch()
          }
          break
        }
        case 'Enter': {
          if (!hasChildren) break
          if (isNativeOpener) {
            event.preventDefault()
            if (showFlyout ? flyoutVisibleRef.current : openRef.current) {
              collapseBranch()
              navKeyboard.focusKey(itemKey)
            } else {
              expandBranch()
            }
          }
          break
        }
        case 'Home': {
          event.preventDefault()
          if (siblings[0]) navKeyboard.focusKey(siblings[0].key)
          break
        }
        case 'End': {
          event.preventDefault()
          const last = siblings[siblings.length - 1]
          if (last) navKeyboard.focusKey(last.key)
          break
        }
        default:
          break
      }
    },
    [collapseBranch, expandBranch, hasChildren, isNativeOpener, itemKey, navKeyboard, parentKey],
  )

  const tabIndex = navKeyboard?.tabStopKey === itemKey ? 0 : -1
  const branchExpanded = showFlyout ? flyoutVisible : open
  const linkRefProps = LinkComponent === NavLink ? { innerRef: linkRef } : { ref: linkRef }

  const link = (
    <LinkComponent
      key={routeKey}
      aria-expanded={hasChildren ? branchExpanded : undefined}
      className={clsx(styles['nav__link'], 'nav__link_sublevel', {
        //eslint-disable-next-line
        [styles['nav__link_opener']]: routes?.length > 0 && !showFlyout,
        ['opened']: open && !showFlyout,
        ['nav__link_opener']: routes?.length > 0 && !showFlyout,
        [styles['nav__link_active']]:
          routes?.length > 0 && ((!open && !showFlyout) || showFlyout) && isBranchActive,
      })}
      data-nav-key={itemKey}
      role={isNativeOpener ? 'button' : undefined}
      tabIndex={tabIndex}
      onClick={() => {
        //eslint-disable-next-line
        if (routes?.length > 0) {
          if (showFlyout) {
            return false
          }

          dispatch({ type: SET_DRAWER_STATE, payload: { collapsed: false } })
          setOpen((current) => !current)
          return false
        }

        return true
      }}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => {
        if (path) {
          preloadDomainForPathname(pathGen(to), routeKey)
        }
      }}
      {...linkRefProps}
      {...(itemConfig?.props || {})}
      {...(isNativeOpener
        ? {}
        : { activeClassName: styles['nav__link_active'], exact, to: pathGen(to) })}
    >
      {icon && (
        <span className={clsx(styles['nav__link-part'], styles['nav__icon'])}>
          <Icon
            name={icon}
            size={'sm'}
          />
        </span>
      )}
      <Typography
        noMargin
        className={clsx(styles['nav__link-part'], styles['nav__text'])}
        variant={'Body-15'}
      >
        {displayLabel}
      </Typography>

      {
        //eslint-disable-next-line
        routes?.length > 0 && !showFlyout && (
          <span className={clsx(styles['nav__link-part'], styles['nav__link_opener'])}>
            <Icon
              className={clsx(styles['nav__link_opener-icon'], {
                [styles['nav__link_opener-icon_open']]: open,
              })}
              name={'arrow-down'}
              size={'sm'}
            />
          </span>
        )
      }
    </LinkComponent>
  )

  return (
    <div
      ref={itemRef}
      className={clsx(styles['nav__item'], {
        [styles['nav__item_opened']]: open && !showFlyout && routes.length > 0,
        [styles['nav__item_flyout']]: showFlyout,
        [styles['nav__item_flyout_visible']]: showFlyout && flyoutVisible && !flyoutClosing,
      })}
      onBlur={handleItemBlur}
      onFocus={handleItemFocus}
      onMouseEnter={handleFlyoutMouseEnter}
      onMouseLeave={handleFlyoutMouseLeave}
    >
      {showTooltip ? (
        <Tooltip
          placement={'right'}
          shown={tooltipFromFocus}
          title={displayLabel}
        >
          {link}
        </Tooltip>
      ) : (
        link
      )}
      {
        //eslint-disable-next-line
        routes?.length > 0 && !showFlyout && (
          <Container
            className={clsx(styles['collapse-wrapper'], {
              ['opened']: open,
            })}
            display={'flex'}
            onFocus={() => {
              if (!open) {
                setOpen(true)
              }
            }}
          >
            <div>
              <Nav
                collapse={collapse}
                items={routes}
                level={level + 1}
                parentKey={itemKey}
              />
            </div>
          </Container>
        )
      }
      {
        //eslint-disable-next-line
        routes?.length > 0 && showFlyout && isFlyoutActive && (
          <Container
            className={clsx(styles['collapse-wrapper'], styles['collapse-wrapper_flyout'], {
              [styles['collapse-wrapper_flyout_visible']]: flyoutVisible && !flyoutClosing,
              [styles['collapse-wrapper_flyout_closing']]: flyoutClosing,
            })}
            display={'flex'}
            style={{
              top: flyoutPosition.top,
              left: flyoutPosition.left,
            }}
          >
            <Typography
              noMargin
              className={styles['collapse-wrapper_flyout__header']}
              color={'Label-Secondary'}
              variant={'Subtitle-15'}
            >
              {displayLabel}
            </Typography>
            <Nav
              collapse={false}
              items={routes}
              level={level + 1}
              parentKey={itemKey}
            />
          </Container>
        )
      }
    </div>
  )
}

export default SubLevel

SubLevel.propTypes = {
  collapse: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.string]),
  path: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  exact: PropTypes.bool,
  routeKey: PropTypes.string,
  itemKey: PropTypes.string.isRequired,
  parentKey: PropTypes.string,
  itemIndex: PropTypes.number,
  label: PropTypes.string,
  label_key: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object),
  level: PropTypes.number.isRequired,
}
